/**
 * QuoteRequestDetail.jsx
 *
 * Página de detalle de una solicitud de cotización.
 * Permite al comprador ver y comparar las cotizaciones de los proveedores.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
    Card,
    Row,
    Col,
    Typography,
    Tag,
    Button,
    Checkbox,
    Select,
    Spin,
    Empty,
    Input,
    message,
    Popconfirm,
    Alert,
    Layout,
    Tooltip,
    Collapse,
    Divider,
    Badge
} from "antd"
import {
    ArrowLeftOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    TrophyOutlined,
    ReloadOutlined,
    ShoppingCartOutlined,
    InfoCircleOutlined,
    SendOutlined,
    DollarOutlined,
    CalendarOutlined,
    UserOutlined
} from "@ant-design/icons"
import { useAuth } from "../contexts/authContext"
import {
    getQuoteRequest,
    acceptQuote,
    createPurchaseOrders,
    getQuoteRequestStatusInfo,
    getProviderResponseStatusInfo,
    formatPrice
} from "../services/QuoteRequestServices"
import { analyzeOrders } from "../services/AIAgentServices"
import "../assets/styles/QuoteRequestDetail.css"

const { Content } = Layout
const { Text } = Typography

const QuoteRequestDetail = () => {
    const [quoteRequest, setQuoteRequest] = useState(null)
    const [loading, setLoading] = useState(true)
    const [acceptingProvider, setAcceptingProvider] = useState(null)
    const [analysisPrompt, setAnalysisPrompt] = useState("")
    const [analysisLoading, setAnalysisLoading] = useState(false)
    const [analysisResult, setAnalysisResult] = useState("")
    const [analysisError, setAnalysisError] = useState("")
    const [analysisProducts, setAnalysisProducts] = useState([])
    const [analysisMeta, setAnalysisMeta] = useState(null)
    const [selectedAspects, setSelectedAspects] = useState([])
    const [orderSelections, setOrderSelections] = useState({})
    const [orderSubmitting, setOrderSubmitting] = useState(false)
    const [orderSummary, setOrderSummary] = useState(null)
    const { quoteRequestId } = useParams()
    const navigate = useNavigate()
    const { getToken } = useAuth()
    const analysisRef = useRef(null)

    const providerLabelById = useMemo(() => {
        const map = {}
            ; (quoteRequest?.detected_providers || []).forEach((provider) => {
                const providerId = provider?.provider_id
                if (!providerId) return
                const providerCompany = provider?.provider_company || provider?.company || ""
                const providerName = provider?.provider_name || provider?.name || "Proveedor"
                map[providerId] = providerCompany || providerName || "Proveedor"
            })
        return map
    }, [quoteRequest])

    const resolveProviderLabel = (providerId, fallbackName) => {
        if (providerId && providerLabelById[providerId]) {
            return providerLabelById[providerId]
        }
        return fallbackName || "Proveedor"
    }

    /**
     * Formatea las notas del proveedor para mejor legibilidad
     * Agrega saltos de línea antes de emojis y en listas de productos
     */
    const formatProviderNotes = (notes) => {
        if (!notes) return ""

        let formatted = notes

        // Agregar salto de línea antes de cada emoji al inicio de "línea"
        formatted = formatted.replace(/([^\n])(📧|💳|📅|📦|✅|❌|⚠️|ℹ️|🔔|📝|🏢|👤|📞|✉️)/g, "$1\n$2")

        // Agregar salto de línea antes de cada bullet point en la lista de productos
        formatted = formatted.replace(/\s{2}•/g, "\n\x20\x20•")

        // Asegurar que haya un salto de línea después de "Productos cotizados:"
        formatted = formatted.replace(/Productos cotizados:/g, "Productos cotizados:\n")

        // Limpiar múltiples saltos de línea consecutivos (máximo 2)
        formatted = formatted.replace(/\n{3,}/g, "\n\n")

        // Trimear el resultado
        formatted = formatted.trim()

        return formatted
    }

    const suggestedAspects = [
        "Precio total",
        "Plazos de entrega",
        "Condiciones de pago",
        "Calidad y reputacion",
        "Riesgos y alertas"
    ]

    const fetchQuoteRequest = useCallback(async () => {
        try {
            setLoading(true)
            const token = await getToken()
            const data = await getQuoteRequest(token, quoteRequestId)
            setQuoteRequest(data)
        } catch (error) {
            console.error("Error fetching quote request:", error)
            message.error("Error al cargar la solicitud de cotización")
        } finally {
            setLoading(false)
        }
    }, [getToken, quoteRequestId])

    useEffect(() => {
        fetchQuoteRequest()
    }, [fetchQuoteRequest])

    const handleAcceptQuote = async (providerId) => {
        try {
            setAcceptingProvider(providerId)
            const token = await getToken()
            await acceptQuote(token, quoteRequestId, providerId)
            message.success("Cotización aceptada. El proveedor ha sido notificado.")
            fetchQuoteRequest()
        } catch (error) {
            console.error("Error accepting quote:", error)
            message.error(error.message || "Error al aceptar la cotización")
        } finally {
            setAcceptingProvider(null)
        }
    }

    const getProviderResponses = useCallback(() => {
        if (!quoteRequest?.responses) return []

        return Object.entries(quoteRequest.responses).map(([providerId, response]) => {
            const providerInfo = quoteRequest.detected_providers?.find(
                (p) => p.provider_id === providerId
            )
            const providerName =
                providerInfo?.provider_name || response?.provider_name || "Proveedor"
            const providerCompany =
                providerInfo?.provider_company ||
                response?.provider_company ||
                providerInfo?.company ||
                response?.company ||
                ""
            const providerLabel = providerCompany || providerName || "Proveedor"
            return {
                providerId,
                providerName,
                providerCompany,
                providerLabel,
                similarityScore: providerInfo?.similarity_score || 0,
                ...response
            }
        })
    }, [quoteRequest])

    const isQuoteAccepted = (providerId) => {
        return quoteRequest?.accepted_quote?.provider_id === providerId
    }

    const hasAcceptedQuote = () => {
        return quoteRequest?.accepted_quote != null
    }

    const isCartRequest = quoteRequest?.type === "cart_request" && quoteRequest?.cart_products?.length > 0

    const productNameById = useMemo(() => {
        return (quoteRequest?.cart_products || []).reduce((acc, product) => {
            const productId = product?.product_id ?? product?.id
            if (!productId) return acc
            const name = product?.product_name || product?.name || productId
            acc[productId] = name
            return acc
        }, {})
    }, [quoteRequest])

    const providerProductMaps = useMemo(() => {
        return (quoteRequest?.detected_providers || []).reduce((acc, provider) => {
            const providerId = provider?.provider_id
            if (!providerId || !Array.isArray(provider.provider_products)) return acc
            const map = provider.provider_products.reduce((mapAcc, item) => {
                const productId = item?.product_id
                if (!productId) return mapAcc
                mapAcc[productId] = item
                return mapAcc
            }, {})
            if (Object.keys(map).length > 0) {
                acc[providerId] = map
            }
            return acc
        }, {})
    }, [quoteRequest])

    const normalizeText = useCallback((value) => {
        if (value === null || value === undefined) return ""
        return String(value).trim().toLowerCase()
    }, [])

    const renderInlineText = (text) => {
        if (!text) return null

        const parts = text.split(/(\*\*[^*]+\*\*)/g)
        return parts.map((part, index) => {
            if (part.startsWith("**") && part.endsWith("**")) {
                const value = part.slice(2, -2)
                return <strong key={`bold-${index}`}>{value}</strong>
            }
            return part
        })
    }

    const renderAnalysisContent = (content) => {
        if (!content) return null

        const lines = content.split(/\r?\n/)
        const blocks = []
        let paragraphLines = []
        let listItems = []

        const flushParagraph = () => {
            if (!paragraphLines.length) return
            const text = paragraphLines.join("\n").trim()
            if (text) {
                blocks.push({ type: "paragraph", text })
            }
            paragraphLines = []
        }

        const flushList = () => {
            if (!listItems.length) return
            blocks.push({ type: "list", items: listItems })
            listItems = []
        }

        lines.forEach((line) => {
            const trimmed = line.trim()
            if (!trimmed) {
                flushParagraph()
                flushList()
                return
            }

            const listMatch = trimmed.match(/^[-*]\s+(.*)$/)
            if (listMatch) {
                flushParagraph()
                listItems.push(listMatch[1])
                return
            }

            if (listItems.length) {
                flushList()
            }

            paragraphLines.push(line)
        })

        flushParagraph()
        flushList()

        return blocks.map((block, index) => {
            if (block.type === "list") {
                return (
                    <ul key={`list-${index}`} className="quote-detail__ai-result-list">
                        {block.items.map((item, itemIndex) => (
                            <li key={`list-item-${index}-${itemIndex}`}>
                                {renderInlineText(item)}
                            </li>
                        ))}
                    </ul>
                )
            }

            return (
                <p key={`paragraph-${index}`} className="quote-detail__ai-result-paragraph">
                    {renderInlineText(block.text)}
                </p>
            )
        })
    }



    const enrichAnalysisProducts = (items) => {
        if (!Array.isArray(items) || items.length === 0) return items || []

        const allQuotes = buildQuoteEntries()
        const quotesById = {}
        allQuotes.forEach(q => {
            if (q.productId) quotesById[q.productId] = q
        })

        return items.map((item) => {
            if (!item || typeof item !== "object") return item

            const reason = item.reason || item.rationale || item.motivo
            const itemId = item.id || item.product_id || item.productId

            if (itemId && quotesById[itemId]) {
                const match = quotesById[itemId]
                return {
                    ...item,
                    product_id: match.productId,
                    requested_product_name: match.requestedName,
                    quoted_product_name: match.quotedName,
                    is_similar_product: match.isSimilar,
                    best_quote_id: match.providerId,
                    best_supplier: match.providerName,
                    best_price: match.price,
                    currency: match.currency,
                    reason: reason
                }
            }

            return {
                ...item,
                reason: reason,
                requested_product_name: item.requested_product_name || item.product_name || "Producto desconocido"
            }
        })
    }

    const parsePriceValue = useCallback((value) => {
        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : null
    }, [])

    const buildQuoteEntries = useCallback(() => {
        const responses = getProviderResponses().filter((item) => item.status === "responded")
        const entries = []

        // Helper para normalizar nombres para matching
        const norm = (str) => String(str || "").toLowerCase().trim().normalize("NFKD").replace(/[\u0300-\u036f]/g, "")

        // Mapa de productos solicitados para matching
        const requestedProductsMap = {}
        if (quoteRequest?.cart_products) {
            quoteRequest.cart_products.forEach(p => {
                const id = p.product_id || p.id
                if (id) {
                    const name = p.product_name || p.name || ""
                    requestedProductsMap[id] = { id, name, normName: norm(name) }
                    // Tambien mapear por nombre para lookup rapido
                    if (name) requestedProductsMap[norm(name)] = id
                }
            })
        } else if (quoteRequest?.original_product_id) {
            const id = quoteRequest.original_product_id
            const name = quoteRequest.original_product_name || ""
            requestedProductsMap[id] = { id, name, normName: norm(name) }
            if (name) requestedProductsMap[norm(name)] = id
        }

        responses.forEach((response) => {
            const providerMap = providerProductMaps[response.providerId] || {}

            // Combinar product_quotes y email_extracted_data
            let rawQuotes = Array.isArray(response.product_quotes) ? [...response.product_quotes] : []

            // Si hay datos extraidos de email, agregarlos si no estan ya en product_quotes
            if (response.email_extracted_data?.products && Array.isArray(response.email_extracted_data.products)) {
                response.email_extracted_data.products.forEach(emailProd => {
                    // Evitar duplicados si ya existe por algun ID (aunque email_prod suele no tener ID)
                    const exists = rawQuotes.some(q => (q.product_id && q.product_id === emailProd.product_id) || (q.name && q.name === emailProd.name))
                    if (!exists) {
                        // Adaptar estructura email_prod a quote
                        rawQuotes.push({
                            ...emailProd,
                            product_name: emailProd.name || emailProd.product_name, // Estandarizar nombre
                            is_email_extracted: true
                        })
                    }
                })
            }

            rawQuotes.forEach((quote) => {
                let quoteId = quote?.product_id ?? quote?.id
                let requestedId = quote?.requested_product_id || providerMap[quoteId]?.requested_product_id

                // Si no hay quoteId (comun en email extraction), intentar inferirlo por nombre
                if (!quoteId && (quote.product_name || quote.name)) {
                    const qName = norm(quote.product_name || quote.name)
                    // Buscar coincidencia exacta en nombres solicitados
                    const matchedId = requestedProductsMap[qName]
                    if (matchedId) {
                        quoteId = matchedId
                        requestedId = matchedId // Asumimos que es este
                    } else {
                        // Busqueda parcial/fuzzy simple
                        const foundId = Object.values(requestedProductsMap).find(rp =>
                            typeof rp === 'object' && rp.normName && (rp.normName.includes(qName) || qName.includes(rp.normName))
                        )?.id
                        if (foundId) {
                            quoteId = foundId
                            requestedId = foundId
                        }
                    }
                }

                // Si seguimos sin ID, usar el nombre como fallback para evitar que se pierda,
                // aunque no hara match con el agente si el agente devuelve ID.
                if (!quoteId) quoteId = `unknown-${normalizeText(quote.product_name || quote.name)}`
                if (!quoteId) return

                if (!requestedId) requestedId = quoteId // Fallback

                const requestedName =
                    quote?.requested_product_name ||
                    providerMap[quoteId]?.requested_product_name ||
                    (requestedId && requestedProductsMap[requestedId]?.name) ||
                    (requestedId ? productNameById[requestedId] : "") ||
                    quoteId

                const quotedName =
                    quote?.product_name ||
                    quote?.name ||
                    providerMap[quoteId]?.product_name ||
                    quote?.quoted_product_name ||
                    requestedName ||
                    quoteId

                const isSimilar =
                    typeof quote?.is_similar_product === "boolean"
                        ? quote.is_similar_product
                        : requestedId && quoteId && !quote.is_email_extracted // En email extraction asumimos match si asignamos ID
                            ? requestedId !== quoteId
                            : false

                const price = parsePriceValue(quote?.price || quote?.unit_price) // price es el total, unit_price es unitario

                // Si no hay precio total pero hay unit_price y quantity, calcular
                const finalPrice = price !== null ? price : (
                    quote?.unit_price && quote?.quantity
                        ? parsePriceValue(quote.unit_price) * parsePriceValue(quote.quantity)
                        : null
                )

                if (finalPrice === null && !quote.is_email_extracted) return

                entries.push({
                    productId: quoteId,
                    requestedProductId: requestedId,
                    requestedName,
                    quotedName,
                    isSimilar,
                    price: finalPrice || 0,
                    currency: quote?.currency || response.currency,
                    providerName: response.providerName,
                    providerLabel: response.providerLabel || response.providerName,
                    providerId: response.providerId,
                    source: quote.is_email_extracted ? 'email' : 'structured'
                })
            })
        })

        return entries
    }, [getProviderResponses, parsePriceValue, productNameById, providerProductMaps, quoteRequest, normalizeText])


    const buildCartLookup = useCallback(() => {
        if (!quoteRequest) return {}

        const lookup = {}
        if (isCartRequest) {
            quoteRequest.cart_products.forEach((product) => {
                const productId = product?.product_id ?? product?.id
                if (!productId) return
                const measures = product?.measures || []
                const quantityFromMeasures = measures.reduce(
                    (sum, measure) => sum + (measure?.quantity || 0),
                    0
                )
                const quantity =
                    product?.quantity !== undefined && product?.quantity !== null
                        ? product.quantity
                        : quantityFromMeasures || null
                const name = product?.product_name || product?.name || productId
                const measureLabel = measures.length
                    ? measures
                        .map((measure) => `${measure.measure}: ${measure.quantity} uds`)
                        .join(", ")
                    : null
                lookup[productId] = {
                    productId,
                    name,
                    quantity,
                    measure: measureLabel
                }
            })
            return lookup
        }

        if (quoteRequest?.original_product_id) {
            lookup[quoteRequest.original_product_id] = {
                productId: quoteRequest.original_product_id,
                name: quoteRequest.original_product_name || quoteRequest.original_product_id,
                quantity: quoteRequest.quantity || 1,
                measure: quoteRequest.original_product_category || null
            }
        }
        return lookup
    }, [isCartRequest, quoteRequest])

    const buildOrderCandidates = useCallback(() => {
        if (!quoteRequest) return []

        const cartLookup = buildCartLookup()
        const candidates = new Map()

        Object.values(cartLookup).forEach((item) => {
            const name = item.name || item.productId
            const key = `original:${normalizeText(name)}`
            candidates.set(key, {
                key,
                name,
                isSimilar: false,
                alternativeOf: null,
                requestedProductId: item.productId,
                requestedProductName: name,
                quantity: item.quantity,
                measure: item.measure,
                quotes: []
            })
        })

        const quoteEntries = buildQuoteEntries()
        const seenQuotes = new Set()

        quoteEntries.forEach((entry) => {
            const baseName = entry.isSimilar ? entry.quotedName : entry.requestedName
            if (!baseName) return

            const key = `${entry.isSimilar ? "similar" : "original"}:${normalizeText(baseName)}`
            let candidate = candidates.get(key)
            if (!candidate) {
                const cartItem = entry.requestedProductId
                    ? cartLookup[entry.requestedProductId]
                    : null
                candidate = {
                    key,
                    name: baseName,
                    isSimilar: entry.isSimilar,
                    alternativeOf: entry.isSimilar ? entry.requestedName : null,
                    requestedProductId: entry.requestedProductId,
                    requestedProductName: entry.requestedName,
                    quantity: cartItem?.quantity ?? null,
                    measure: cartItem?.measure ?? null,
                    quotes: []
                }
                candidates.set(key, candidate)
            } else {
                if (entry.isSimilar && !candidate.alternativeOf && entry.requestedName) {
                    candidate.alternativeOf = entry.requestedName
                }
                if (!candidate.requestedProductId && entry.requestedProductId) {
                    candidate.requestedProductId = entry.requestedProductId
                }
                if (!candidate.requestedProductName && entry.requestedName) {
                    candidate.requestedProductName = entry.requestedName
                }
                if (candidate.quantity == null && entry.requestedProductId) {
                    const cartItem = cartLookup[entry.requestedProductId]
                    if (cartItem) {
                        candidate.quantity = cartItem.quantity
                        candidate.measure = cartItem.measure
                    }
                }
            }

            const quoteKey = `${key}:${entry.providerId}:${entry.productId}`
            if (seenQuotes.has(quoteKey)) return
            seenQuotes.add(quoteKey)

            candidate.quotes.push({
                providerId: entry.providerId,
                providerName: entry.providerName,
                providerLabel: entry.providerLabel || entry.providerName,
                price: entry.price,
                currency: entry.currency,
                productId: entry.productId,
                requestedProductId: entry.requestedProductId,
                requestedProductName: entry.requestedName,
                quotedProductName: entry.quotedName,
                isSimilar: entry.isSimilar
            })
        })

        const results = []
        candidates.forEach((candidate) => {
            results.push({ ...candidate, bestQuote: null })
        })

        results.sort((a, b) => a.name.localeCompare(b.name))
        return results
    }, [buildCartLookup, buildQuoteEntries, normalizeText, quoteRequest])

    const resolvePreferredQuote = useCallback(
        (quotes, analysisItem) => {
            if (!analysisItem || !Array.isArray(quotes) || quotes.length === 0) {
                return null
            }

            // 1. Prioridad: Buscar por ID exacto del producto (ID de la cotización)
            // Esto permite que el agente seleccione una opción específica directamente por su ID
            const productId = analysisItem.product_id || analysisItem.id || analysisItem.productId
            if (productId) {
                const byProductId = quotes.find(q => {
                    const qId = q.productId || q.id
                    return qId && String(qId).trim() === String(productId).trim()
                })
                if (byProductId) return byProductId
            }

            const bestSupplier = normalizeText(
                analysisItem.best_supplier || analysisItem.supplier || analysisItem.provider
            )
            const rawQuoteId =
                analysisItem.best_quote_id || analysisItem.quote_id || analysisItem.best_quote
            const bestQuoteId = rawQuoteId ? String(rawQuoteId).trim() : ""

            if (bestQuoteId) {
                const byId = quotes.find((quote) => quote.providerId === bestQuoteId)
                if (byId) return byId
                const byName = quotes.find(
                    (quote) => normalizeText(quote.providerName) === normalizeText(bestQuoteId)
                )
                if (byName) return byName
                const byLabel = quotes.find(
                    (quote) => normalizeText(quote.providerLabel) === normalizeText(bestQuoteId)
                )
                if (byLabel) return byLabel
                const byPartial = quotes.find(
                    (quote) => quote.providerId && bestQuoteId.includes(quote.providerId)
                )
                if (byPartial) return byPartial
            }

            if (bestSupplier) {
                const bySupplier = quotes.find(
                    (quote) => normalizeText(quote.providerName) === bestSupplier
                )
                if (bySupplier) return bySupplier
                const byLabel = quotes.find(
                    (quote) => normalizeText(quote.providerLabel) === bestSupplier
                )
                if (byLabel) return byLabel
            }

            return null
        },
        [normalizeText]
    )

    const mergeRecommendedProducts = useCallback(
        (candidates, items) => {
            if (!Array.isArray(items) || items.length === 0) {
                return []
            }

            const candidateMap = {}
            candidates.forEach((candidate) => {
                if (!candidate?.key) return
                candidateMap[candidate.key] = candidate
            })

            const results = []

            items.forEach((analysisItem) => {
                if (!analysisItem || typeof analysisItem !== "object") return
                const requestedName =
                    analysisItem.requested_product_name ||
                    analysisItem.product_name ||
                    analysisItem.product ||
                    analysisItem.name
                if (!requestedName) return

                const quotedName = analysisItem.quoted_product_name
                const isSimilar =
                    typeof analysisItem.is_similar_product === "boolean"
                        ? analysisItem.is_similar_product
                        : quotedName && quotedName !== requestedName
                const displayName = isSimilar && quotedName ? quotedName : requestedName
                const candidateKey = `${isSimilar ? "similar" : "original"}:${normalizeText(
                    displayName
                )}`
                const candidate = candidateMap[candidateKey]
                const quotes = candidate?.quotes || []
                const preferredQuote = resolvePreferredQuote(quotes, analysisItem)

                const bestPrice =
                    analysisItem.best_price ?? analysisItem.price ?? preferredQuote?.price ?? null
                const bestSupplier =
                    analysisItem.best_supplier ||
                    analysisItem.supplier ||
                    analysisItem.provider ||
                    preferredQuote?.providerName ||
                    null
                const bestQuoteId =
                    analysisItem.best_quote_id ||
                    analysisItem.quote_id ||
                    analysisItem.best_quote ||
                    preferredQuote?.providerId ||
                    null
                const currency = analysisItem.currency || preferredQuote?.currency
                const reason =
                    analysisItem.reason || analysisItem.rationale || analysisItem.motivo

                results.push({
                    ...analysisItem,
                    key: candidate?.key || candidateKey,
                    product_name: displayName,
                    requested_product_name: requestedName,
                    quoted_product_name: quotedName,
                    is_similar_product: Boolean(isSimilar),
                    alternative_of:
                        analysisItem.alternative_of ||
                        candidate?.alternativeOf ||
                        (isSimilar ? requestedName : null),
                    quotes,
                    best_price: bestPrice,
                    currency,
                    best_supplier: bestSupplier,
                    best_quote_id: bestQuoteId,
                    reason,
                    quantity: candidate?.quantity ?? null,
                    measure: candidate?.measure ?? null,
                    bestQuote: preferredQuote
                })
            })

            return results
        },
        [normalizeText, resolvePreferredQuote]
    )

    // Renderiza la vista de cards de cotizaciones (más legible que la tabla)
    const renderQuoteCards = () => {
        const respondedQuotes = responses.filter(r => r.status === "responded" || r.status === "draft")
        const pendingQuotes = responses.filter(r => r.status === "pending")
        const declinedQuotes = responses.filter(r => r.status === "declined")

        const renderQuoteCard = (record) => {
            const statusInfo = getProviderResponseStatusInfo(record.status)
            const isAccepted = isQuoteAccepted(record.providerId)
            const productQuotes = record.product_quotes || []
            const providerMap = record.providerId ? providerProductMaps[record.providerId] : null

            return (
                <div
                    key={record.providerId}
                    className={`quote-card ${isAccepted ? 'quote-card--accepted' : ''} ${record.status === 'draft' ? 'quote-card--draft' : ''}`}
                >
                    {/* Header del proveedor */}
                    <div className="quote-card__header">
                        <div className="quote-card__provider-info">
                            <div className="quote-card__provider-name">
                                <UserOutlined style={{ marginRight: 8 }} />
                                {record.providerLabel || record.providerName || "Proveedor"}
                                {isAccepted && (
                                    <Tag color="gold" icon={<TrophyOutlined />} style={{ marginLeft: 8 }}>
                                        Aceptado
                                    </Tag>
                                )}
                            </div>
                            <div className="quote-card__provider-meta">
                                <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
                                <span className="quote-card__similarity">
                                    Similitud: {(record.similarityScore * 100).toFixed(0)}%
                                </span>
                            </div>
                        </div>
                        <div className="quote-card__summary">
                            <div className="quote-card__total-price">
                                <DollarOutlined style={{ marginRight: 4 }} />
                                {formatPrice(record.price, record.currency)}
                            </div>
                            <div className="quote-card__delivery">
                                <CalendarOutlined style={{ marginRight: 4 }} />
                                {record.delivery_days > 0
                                    ? `${record.delivery_days} días`
                                    : <Text type="secondary" style={{ fontSize: 12 }}>Plazo pendiente</Text>
                                }
                            </div>
                        </div>
                    </div>

                    {/* Detalle de productos/medidas */}
                    {productQuotes.length > 0 && (
                        <div className="quote-card__products">
                            <Divider style={{ margin: '12px 0' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Detalle por producto</Text>
                            </Divider>
                            <div className="quote-card__products-grid">
                                {productQuotes.map((quote, index) => {
                                    const productId = quote?.product_id ?? quote?.id
                                    const providerProduct = providerMap ? providerMap[productId] : null
                                    const name = providerProduct?.product_name || quote?.product_name || productNameById[productId] || `Producto ${index + 1}`
                                    const measureQuotes = quote?.measure_quotes
                                    const currency = quote?.currency || record.currency

                                    // Buscar las medidas originales solicitadas para este producto
                                    // Para productos "similares", el productId es el del proveedor, no el original
                                    // Debemos buscar también por requested_product_id
                                    const requestedProductId = quote?.requested_product_id || providerProduct?.requested_product_id
                                    const originalProduct = quoteRequest?.cart_products?.find(
                                        p => (p.product_id || p.id) === productId ||
                                            (requestedProductId && (p.product_id || p.id) === requestedProductId)
                                    )
                                    const originalMeasures = originalProduct?.measures || []

                                    return (
                                        <div key={productId || index} className="quote-card__product">
                                            <div className="quote-card__product-header">
                                                <Text strong>{name}</Text>
                                                <Text className="quote-card__product-total">
                                                    {formatPrice(quote.price, currency)}
                                                </Text>
                                            </div>

                                            {measureQuotes && measureQuotes.length > 0 ? (
                                                // Mostrar cotización con precios por medida
                                                <table className="quote-card__measures-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Medida</th>
                                                            <th>P. Unit.</th>
                                                            <th>Cant.</th>
                                                            <th>Subtotal</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {measureQuotes.map((mq, mIndex) => (
                                                            <tr key={mIndex}>
                                                                <td>{mq.measure}</td>
                                                                <td className="text-right">{formatPrice(mq.unit_price, currency)}</td>
                                                                <td className="text-center">{mq.quantity}</td>
                                                                <td className="text-right">{formatPrice(mq.subtotal, currency)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : originalMeasures.length > 0 ? (
                                                // Cotización antigua: mostrar medidas solicitadas sin desglose de precio
                                                <div className="quote-card__measures-info">
                                                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                                        Medidas solicitadas:
                                                    </Text>
                                                    <div className="quote-card__measures-tags">
                                                        {originalMeasures.map((m, mIdx) => (
                                                            <Tag key={mIdx} color="blue">
                                                                {m.measure}: {m.quantity} uds
                                                            </Tag>
                                                        ))}
                                                    </div>
                                                    {quote.unit_price && (
                                                        <div className="quote-card__simple-price" style={{ marginTop: 8 }}>
                                                            <Text type="secondary">Precio unitario: </Text>
                                                            <Text>{formatPrice(quote.unit_price, currency)}</Text>
                                                        </div>
                                                    )}
                                                    <Alert
                                                        type="info"
                                                        showIcon
                                                        message="Esta cotización no tiene desglose por medida"
                                                        description="El proveedor cotizó un precio general. Las nuevas cotizaciones mostrarán el precio por cada medida."
                                                        style={{ marginTop: 8, fontSize: 12 }}
                                                    />
                                                </div>
                                            ) : quote.unit_price ? (
                                                <div className="quote-card__simple-price">
                                                    <span>{formatPrice(quote.unit_price, currency)} × {quote.quantity} uds</span>
                                                </div>
                                            ) : null}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}

                    {/* Notas del proveedor */}
                    {record.notes && (
                        <div className="quote-card__notes">
                            <Text type="secondary" style={{ fontSize: 12 }}>Notas del proveedor:</Text>
                            <div className="quote-card__notes-content">{formatProviderNotes(record.notes)}</div>
                        </div>
                    )}

                    {/* Acciones */}
                    <div className="quote-card__actions">
                        {record.status === "draft" ? (
                            <Tooltip title="Cotización procesada automáticamente. Esperando confirmación del proveedor.">
                                <Tag color="orange" icon={<InfoCircleOutlined />}>
                                    Borrador - Pendiente de confirmación
                                </Tag>
                            </Tooltip>
                        ) : record.status === "responded" && !isAccepted && !hasAcceptedQuote() ? (
                            <Popconfirm
                                title="¿Aceptar esta cotización?"
                                description="El proveedor será notificado."
                                onConfirm={() => handleAcceptQuote(record.providerId)}
                                okText="Sí, aceptar"
                                cancelText="No"
                            >
                                <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    loading={acceptingProvider === record.providerId}
                                >
                                    Aceptar Cotización
                                </Button>
                            </Popconfirm>
                        ) : isAccepted ? (
                            <Tag color="success" icon={<CheckCircleOutlined />}>
                                Cotización Aceptada
                            </Tag>
                        ) : hasAcceptedQuote() ? (
                            <Text type="secondary">No seleccionado</Text>
                        ) : null}
                    </div>
                </div>
            )
        }

        return (
            <div className="quote-cards-container">
                {/* Cotizaciones respondidas */}
                {respondedQuotes.length > 0 && (
                    <div className="quote-cards-section">
                        <div className="quote-cards-section__header">
                            <Badge count={respondedQuotes.length} style={{ backgroundColor: '#52c41a' }}>
                                <Text strong style={{ fontSize: 16 }}>Cotizaciones Recibidas</Text>
                            </Badge>
                        </div>
                        <div className="quote-cards-list">
                            {respondedQuotes.map(renderQuoteCard)}
                        </div>
                    </div>
                )}

                {/* Cotizaciones pendientes */}
                {pendingQuotes.length > 0 && (
                    <div className="quote-cards-section">
                        <div className="quote-cards-section__header">
                            <Badge count={pendingQuotes.length} style={{ backgroundColor: '#faad14' }}>
                                <Text strong style={{ fontSize: 16 }}>Esperando Respuesta</Text>
                            </Badge>
                        </div>
                        <div className="quote-cards-pending">
                            {pendingQuotes.map(record => (
                                <div key={record.providerId} className="quote-card-pending">
                                    <ClockCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                                    <Text>{record.providerLabel || record.providerName}</Text>
                                    <Tag color="orange" style={{ marginLeft: 8 }}>Pendiente</Tag>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Cotizaciones rechazadas */}
                {declinedQuotes.length > 0 && (
                    <Collapse ghost>
                        <Collapse.Panel
                            header={
                                <Badge count={declinedQuotes.length} style={{ backgroundColor: '#ff4d4f' }}>
                                    <Text type="secondary">Rechazadas</Text>
                                </Badge>
                            }
                            key="declined"
                        >
                            <div className="quote-cards-declined">
                                {declinedQuotes.map(record => (
                                    <div key={record.providerId} className="quote-card-declined">
                                        <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                                        <Text type="secondary">{record.providerLabel || record.providerName}</Text>
                                    </div>
                                ))}
                            </div>
                        </Collapse.Panel>
                    </Collapse>
                )}

                {responses.length === 0 && (
                    <Empty description="Aún no hay respuestas de proveedores">
                        <Text type="secondary">
                            Los proveedores contactados recibirán una notificación y podrán enviar sus cotizaciones.
                        </Text>
                    </Empty>
                )}
            </div>
        )
    }

    // Calcular total de items
    const getTotalItems = () => {
        if (!quoteRequest) return 0
        const isCart = quoteRequest.type === "cart_request" && quoteRequest.cart_products?.length > 0
        if (isCart) {
            return quoteRequest.cart_products.reduce((sum, prod) => {
                const prodQty = prod.measures?.reduce((s, m) => s + (m.quantity || 0), 0) || prod.quantity || 0
                return sum + prodQty
            }, 0)
        }
        return quoteRequest.quantity || 0
    }

    const formatBestPrice = (value) => {
        if (value === null || value === undefined || value === "") {
            return null
        }
        if (typeof value === "number" && Number.isFinite(value)) {
            return `$${value.toLocaleString("es-AR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`
        }
        return String(value)
    }

    const extractJsonBlock = (text, key) => {
        if (!text || !key) {
            return null
        }
        const lower = text.toLowerCase()
        const idx = lower.indexOf(key.toLowerCase())
        if (idx < 0) {
            return null
        }
        const start = text.lastIndexOf("{", idx)
        if (start < 0) {
            return null
        }
        let depth = 0
        let inString = false
        let escape = false
        for (let i = start; i < text.length; i += 1) {
            const char = text[i]
            if (escape) {
                escape = false
                continue
            }
            if (char === "\\" && inString) {
                escape = true
                continue
            }
            if (char === "\"") {
                inString = !inString
                continue
            }
            if (inString) {
                continue
            }
            if (char === "{") {
                depth += 1
            } else if (char === "}") {
                depth -= 1
                if (depth === 0) {
                    return text.slice(start, i + 1)
                }
            }
        }
        return null
    }

    const stripEmbeddedJson = (text) => {
        if (!text) {
            return ""
        }
        let cleaned = text.replace(
            /<per_product_json>[\s\S]*?<\/per_product_json>/gi,
            ""
        )
        const tagIndex = cleaned.toLowerCase().indexOf("<per_product_json")
        if (tagIndex >= 0 && cleaned.toLowerCase().indexOf("</per_product_json>") === -1) {
            cleaned = cleaned.slice(0, tagIndex)
        }
        cleaned = cleaned.replace(/```[\s\S]*?```/g, "")
        const fenceStart = cleaned.indexOf("```")
        if (fenceStart >= 0) {
            cleaned = cleaned.slice(0, fenceStart)
        }
        const jsonBlock = extractJsonBlock(cleaned, "per_product")
        if (jsonBlock) {
            cleaned = cleaned.replace(jsonBlock, "")
        }
        return cleaned.trim()
    }

    const extractPerProductFromText = (text) => {
        if (!text || !text.includes("per_product")) {
            return []
        }
        const tagMatch = text.match(/<per_product_json>([\s\S]*?)<\/per_product_json>/i)
        let payload = tagMatch ? tagMatch[1].trim() : ""
        if (!payload) {
            const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/i)
            if (fenceMatch && fenceMatch[1].includes("per_product")) {
                payload = fenceMatch[1].trim()
            }
        }
        if (!payload) {
            payload = extractJsonBlock(text, "per_product") || ""
        }
        if (payload) {
            try {
                const parsed = JSON.parse(payload)
                if (Array.isArray(parsed)) {
                    return parsed.filter((item) => item && typeof item === "object")
                }
                if (parsed && typeof parsed === "object") {
                    const items = parsed.per_product || parsed.productos || parsed.por_producto
                    if (Array.isArray(items)) {
                        return items.filter((item) => item && typeof item === "object")
                    }
                }
            } catch (error) {
                // continue to fallback parsing
            }
        }
        const matches = text.match(/{[^{}]*"product_name"[^{}]*}/g) || []
        const items = []
        matches.forEach((match) => {
            try {
                const parsed = JSON.parse(match)
                if (parsed && typeof parsed === "object") {
                    items.push(parsed)
                }
            } catch (error) {
                // ignore invalid chunks
            }
        })
        return items
    }

    const buildProductItems = () => {
        if (!quoteRequest) {
            return []
        }

        if (isCartRequest) {
            return quoteRequest.cart_products.map((prod) => {
                const measures = prod.measures || []
                const totalQty = measures.reduce((sum, m) => sum + (m.quantity || 0), 0)
                const measureLabel = measures.length
                    ? measures.map((m) => `${m.measure}: ${m.quantity} uds`).join(", ")
                    : undefined
                return {
                    product_id: prod.product_id || prod.id,
                    name: prod.product_name,
                    quantity: prod.quantity || totalQty || null,
                    measure: measureLabel
                }
            })
        }

        return [
            {
                product_id: quoteRequest.original_product_id,
                name: quoteRequest.original_product_name,
                quantity: quoteRequest.quantity || null,
                measure: quoteRequest.original_product_category || undefined
            }
        ]
    }

    const buildOrdersForAnalysis = () => {
        const productItems = buildProductItems()
        const providerResponses = getProviderResponses().filter(
            (item) => item.status === "responded" || item.status === "draft"
        )

        return providerResponses.map((response) => ({
            id: `${quoteRequest.id}-${response.providerId}`,
            quote_number: quoteRequest.id,
            status: response.status,
            date: response.responded_at,
            total: response.price
                ? { amount: response.price, currency: response.currency }
                : undefined,
            supplier: response.providerLabel || response.providerName,
            deliveryTerm: response.delivery_days ? `${response.delivery_days} dias` : undefined,
            notes: response.notes,
            products: productItems.map((item) => {
                const productId = item.product_id || item.id
                if (!productId) {
                    return item
                }

                const requestedName =
                    item.requested_product_name ||
                    item.name ||
                    item.product_name ||
                    productNameById[productId] ||
                    productId

                const baseItem = {
                    ...item,
                    requested_product_id: item.requested_product_id || productId,
                    requested_product_name: requestedName,
                    is_similar_product: false
                }

                if (!Array.isArray(response.product_quotes) || response.product_quotes.length === 0) {
                    return baseItem
                }
                const providerMap = providerProductMaps[response.providerId] || {}
                const quote = response.product_quotes.find((q) => {
                    const quoteId = q?.product_id ?? q?.id
                    const requestedId =
                        q?.requested_product_id ||
                        providerMap[quoteId]?.requested_product_id ||
                        quoteId
                    return requestedId === productId
                })
                if (!quote) {
                    return baseItem
                }
                const quoteProductId = quote?.product_id ?? quote?.id ?? productId
                const requestedId =
                    quote?.requested_product_id ||
                    providerMap[quoteProductId]?.requested_product_id ||
                    productId
                const requestedNameFromQuote =
                    quote?.requested_product_name ||
                    providerMap[quoteProductId]?.requested_product_name ||
                    requestedName
                const quotedName =
                    quote?.product_name ||
                    providerMap[quoteProductId]?.product_name ||
                    requestedNameFromQuote ||
                    quoteProductId
                const isSimilar =
                    typeof quote?.is_similar_product === "boolean"
                        ? quote.is_similar_product
                        : requestedId && quoteProductId
                            ? requestedId !== quoteProductId
                            : false
                return {
                    ...baseItem,
                    name: requestedNameFromQuote || baseItem.name,
                    product_name: requestedNameFromQuote || baseItem.product_name,
                    requested_product_id: requestedId,
                    requested_product_name: requestedNameFromQuote,
                    quoted_product_id: quoteProductId,
                    quoted_product_name: quotedName,
                    is_similar_product: isSimilar,
                    price: quote.price,
                    currency: quote.currency || response.currency
                }
            })
        }))
    }

    const handleAnalyzeQuotes = async () => {
        const promptText = analysisPrompt.trim()
        if (!promptText) {
            message.warning("Escribí un prompt para el análisis")
            return
        }

        try {
            setAnalysisLoading(true)
            setAnalysisError("")
            setAnalysisResult("")
            setAnalysisMeta(null)
            setAnalysisProducts([])

            const responded = getProviderResponses().filter(
                (item) => item.status === "responded" || item.status === "draft"
            )
            if (responded.length === 0) {
                setAnalysisError("Aún no hay cotizaciones respondidas para analizar.")
                return
            }

            const orders = buildOrdersForAnalysis()
            const token = await getToken()
            const preferences =
                selectedAspects.length > 0 ? { aspectos: selectedAspects } : undefined
            const response = await analyzeOrders(orders, promptText, token, preferences)
            const rawAnalysis = typeof response?.analysis === "string" ? response.analysis.trim() : ""
            let perProduct = Array.isArray(response?.per_product) ? response.per_product : []
            if (perProduct.length === 0 && response?.per_product && typeof response.per_product === "object") {
                const nested = response.per_product.per_product
                if (Array.isArray(nested)) {
                    perProduct = nested
                }
            }
            if (perProduct.length === 0 && typeof response?.per_product === "string") {
                try {
                    const parsed = JSON.parse(response.per_product)
                    if (Array.isArray(parsed)) {
                        perProduct = parsed
                    } else if (parsed && Array.isArray(parsed.per_product)) {
                        perProduct = parsed.per_product
                    }
                } catch (error) {
                    // ignore parsing error
                }
            }
            if (perProduct.length === 0 && rawAnalysis.includes("per_product")) {
                perProduct = extractPerProductFromText(rawAnalysis)
            }
            const analysisText = stripEmbeddedJson(rawAnalysis)

            if (!analysisText && perProduct.length === 0) {
                setAnalysisError("El agente no devolvio un resultado util.")
                return
            }

            setAnalysisResult(
                analysisText ||
                "Analisis generado. Revisa la comparacion por producto para ver la recomendacion."
            )
            setAnalysisProducts(enrichAnalysisProducts(perProduct))
            setAnalysisMeta({
                count: responded.length,
                model: response?.model,
                finishReason: response?.finish_reason,
                generatedAt: new Date()
            })
        } catch (error) {
            console.error("Error analyzing quotes:", error)
            const detail = error?.message || "No se pudo generar el análisis"
            setAnalysisError(detail)
            message.error(detail)
        } finally {
            setAnalysisLoading(false)
        }
    }

    const handleAspectToggle = (aspect, checked) => {
        setSelectedAspects((prev) => {
            if (checked) {
                return prev.includes(aspect) ? prev : [...prev, aspect]
            }
            return prev.filter((item) => item !== aspect)
        })
    }

    const handleClearAspects = () => {
        setSelectedAspects([])
    }

    const analysisStatus = analysisLoading
        ? { color: "blue", label: "Analizando" }
        : analysisError
            ? { color: "red", label: "Error" }
            : analysisResult
                ? { color: "green", label: "Listo" }
                : { color: "gold", label: "Pendiente" }
    const orderCandidates = useMemo(() => buildOrderCandidates(), [buildOrderCandidates])
    const recommendedProducts = useMemo(() => {
        if (!analysisProducts.length) {
            return []
        }
        return mergeRecommendedProducts(orderCandidates, analysisProducts)
    }, [analysisProducts, mergeRecommendedProducts, orderCandidates])

    useEffect(() => {
        if (!recommendedProducts.length) {
            setOrderSelections({})
            return
        }

        setOrderSelections((prev) => {
            const next = { ...prev }
            recommendedProducts.forEach((product) => {
                const key = product?.key
                if (!key) return
                const existing = prev[key] || {}
                const bestQuote = product.bestQuote

                next[key] = {
                    selected: existing.selected || false,
                    providerId: existing.providerId || bestQuote?.providerId,
                    productId: existing.productId || bestQuote?.productId,
                    requestedProductId:
                        existing.requestedProductId || bestQuote?.requestedProductId
                }
            })
            return next
        })
    }, [recommendedProducts])

    const resolveSelectedQuote = (product, selection) => {
        const quotes = Array.isArray(product?.quotes) ? product.quotes : []
        if (selection?.providerId) {
            const match = quotes.find((quote) => quote.providerId === selection.providerId)
            if (match) return match
        }
        return product?.bestQuote || null
    }

    const handleToggleOrderItem = (key, checked) => {
        setOrderSelections((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                selected: checked
            }
        }))
    }

    const handleOrderProviderChange = (key, providerId, product) => {
        const quotes = Array.isArray(product?.quotes) ? product.quotes : []
        const selectedQuote = quotes.find((quote) => quote.providerId === providerId)
        setOrderSelections((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                providerId,
                productId: selectedQuote?.productId || prev[key]?.productId,
                requestedProductId:
                    selectedQuote?.requestedProductId || prev[key]?.requestedProductId
            }
        }))
    }

    const handleSelectAllOrders = () => {
        setOrderSelections((prev) => {
            const next = { ...prev }
            recommendedProducts.forEach((product) => {
                const key = product?.key
                if (!key) return
                const bestQuote = product.bestQuote
                if (!bestQuote) return
                next[key] = {
                    selected: true,
                    providerId: prev[key]?.providerId || bestQuote.providerId,
                    productId: prev[key]?.productId || bestQuote.productId,
                    requestedProductId:
                        prev[key]?.requestedProductId || bestQuote.requestedProductId
                }
            })
            return next
        })
    }

    const handleClearOrderSelection = () => {
        setOrderSelections((prev) => {
            const next = { ...prev }
            Object.keys(next).forEach((key) => {
                next[key] = { ...next[key], selected: false }
            })
            return next
        })
    }

    const buildOrderPayload = () => {
        const items = []
        const missing = []
        recommendedProducts.forEach((product) => {
            const key = product?.key
            if (!key) return
            const selection = orderSelections[key]
            if (!selection?.selected) return
            const selectedQuote = resolveSelectedQuote(product, selection)
            if (!selectedQuote) {
                missing.push(product?.product_name || product?.name || key)
                return
            }
            items.push({
                provider_id: selectedQuote.providerId,
                product_id: selectedQuote.productId,
                requested_product_id: selectedQuote.requestedProductId
            })
        })
        return { items, missing }
    }

    const orderSummaryData = useMemo(() => {
        const providers = {}
        let selectedCount = 0

        recommendedProducts.forEach((product) => {
            const key = product?.key
            if (!key) return
            const selection = orderSelections[key]
            if (!selection?.selected) return

            const selectedQuote = resolveSelectedQuote(product, selection)
            if (!selectedQuote) return

            selectedCount += 1
            const providerId = selectedQuote.providerId
            if (!providers[providerId]) {
                providers[providerId] = {
                    providerId,
                    providerName: selectedQuote.providerLabel || selectedQuote.providerName || "Proveedor",
                    items: [],
                    total: 0,
                    currency: selectedQuote.currency || null,
                    totalValid: true
                }
            }

            const entry = providers[providerId]
            entry.items.push(product?.product_name || product?.name || "Producto")

            if (typeof selectedQuote.price === "number") {
                entry.total += selectedQuote.price
            } else {
                entry.totalValid = false
            }

            if (entry.currency && selectedQuote.currency && entry.currency !== selectedQuote.currency) {
                entry.totalValid = false
            }
        })

        const providerList = Object.values(providers)
        return {
            selectedCount,
            providerCount: providerList.length,
            providers: providerList
        }
    }, [recommendedProducts, orderSelections])

    const handleCreateOrders = async () => {
        const { items, missing } = buildOrderPayload()
        if (missing.length > 0) {
            message.error("Hay productos seleccionados sin cotizacion valida.")
            return
        }
        if (items.length === 0) {
            message.warning("Selecciona al menos un producto para pedir.")
            return
        }

        try {
            setOrderSubmitting(true)
            const token = await getToken()
            const response = await createPurchaseOrders(token, quoteRequestId, items)
            setOrderSummary(response?.data || response)
            message.success("Pedidos enviados a los proveedores.")
        } catch (error) {
            console.error("Error creating orders:", error)
            message.error(error?.message || "Error al crear los pedidos.")
        } finally {
            setOrderSubmitting(false)
        }
    }

    if (loading) {
        return (
            <Layout className="quote-detail__page">
                <Content className="quote-detail__layout-inner">
                    <div style={{ textAlign: "center", padding: 100 }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16, color: "#666" }}>Cargando solicitud...</p>
                    </div>
                </Content>
            </Layout>
        )
    }

    if (!quoteRequest) {
        return (
            <Layout className="quote-detail__page">
                <Content className="quote-detail__layout-inner">
                    <div className="quote-detail__content">
                        <Empty description="Solicitud no encontrada">
                            <Button onClick={() => navigate("/mis-cotizaciones")}>
                                Volver a Mis Solicitudes
                            </Button>
                        </Empty>
                    </div>
                </Content>
            </Layout>
        )
    }

    const responses = getProviderResponses()
    const respondedCount = responses.filter((r) => r.status === "responded" || r.status === "draft").length
    const statusInfo = getQuoteRequestStatusInfo(quoteRequest.status)
    const acceptedProviderInfo = quoteRequest.detected_providers?.find(
        (provider) => provider.provider_id === quoteRequest.accepted_quote?.provider_id
    )
    const acceptedProviderLabel =
        acceptedProviderInfo?.provider_company ||
        acceptedProviderInfo?.provider_name ||
        "un proveedor"

    return (
        <Layout className="quote-detail__page">
            <Content className="quote-detail__layout-inner">
                {/* Header */}
                <div className="quote-detail__header">
                    <div className="quote-detail__header-left">
                        <Button
                            className="quote-detail__back-btn"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate("/mis-cotizaciones")}
                        >
                            Volver
                        </Button>
                        <p className="quote-detail__eyebrow">Detalle de solicitud</p>
                        <h1 className="quote-detail__title">
                            {isCartRequest ? (
                                <>
                                    <ShoppingCartOutlined style={{ marginRight: 8 }} />
                                    Cotización de {quoteRequest.cart_products.length} producto(s)
                                </>
                            ) : (
                                quoteRequest.original_product_name || "Solicitud de Cotización"
                            )}
                        </h1>
                        <p className="quote-detail__subtitle">
                            Creada el {new Date(quoteRequest.created_at).toLocaleDateString("es-AR", {
                                day: "numeric",
                                month: "long",
                                year: "numeric"
                            })}
                        </p>
                    </div>
                    <div className="quote-detail__actions">
                        <Button icon={<ReloadOutlined />} onClick={fetchQuoteRequest}>
                            Actualizar
                        </Button>
                    </div>
                </div>

                <div className="quote-detail__content">
                    {/* Alerta si hay cotización aceptada */}
                    {hasAcceptedQuote() && (
                        <Alert
                            className="quote-detail__alert"
                            type="success"
                            showIcon
                            icon={<TrophyOutlined />}
                            message="Cotización Aceptada"
                            description={`Has aceptado la cotización de ${acceptedProviderLabel}. El proveedor ha sido notificado y puedes contactarlo para continuar.`}
                        />
                    )}

                    <Row gutter={[24, 24]}>
                        {/* Información de la solicitud */}
                        <Col xs={24} lg={8}>
                            <Card className="quote-detail__card" title="Información">
                                {/* Estado */}
                                <div className="quote-detail__info-section">
                                    <div className="quote-detail__info-label">Estado</div>
                                    <Tag color={statusInfo.color} style={{ marginTop: 4 }}>
                                        {statusInfo.label}
                                    </Tag>
                                </div>

                                {/* Productos */}
                                {isCartRequest ? (
                                    <div className="quote-detail__info-section">
                                        <div className="quote-detail__info-label">Productos solicitados</div>
                                        <div className="quote-detail__products-list">
                                            {quoteRequest.cart_products.map((prod, idx) => (
                                                <div key={idx} className="quote-detail__product-item">
                                                    <div className="quote-detail__product-name">
                                                        {prod.product_name}
                                                    </div>
                                                    {prod.measures?.length > 0 && (
                                                        <div className="quote-detail__product-measures">
                                                            {prod.measures.map((m, mIdx) => (
                                                                <Tag
                                                                    key={mIdx}
                                                                    className="quote-detail__measure-tag"
                                                                >
                                                                    {m.measure}: {m.quantity} uds
                                                                </Tag>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="quote-detail__info-section">
                                            <div className="quote-detail__info-label">Producto</div>
                                            <div className="quote-detail__info-value">
                                                {quoteRequest.original_product_name}
                                            </div>
                                        </div>
                                        {quoteRequest.original_product_category && (
                                            <div className="quote-detail__info-section">
                                                <div className="quote-detail__info-label">Categoría</div>
                                                <div className="quote-detail__info-value">
                                                    {quoteRequest.original_product_category}
                                                </div>
                                            </div>
                                        )}
                                        <div className="quote-detail__info-section">
                                            <div className="quote-detail__info-label">Cantidad</div>
                                            <div className="quote-detail__info-value">
                                                {quoteRequest.quantity} unidades
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Especificaciones */}
                                {quoteRequest.specifications && (
                                    <div className="quote-detail__info-section">
                                        <div className="quote-detail__info-label">Especificaciones</div>
                                        <div className="quote-detail__info-value">
                                            {quoteRequest.specifications}
                                        </div>
                                    </div>
                                )}

                                {/* Fecha límite */}
                                {quoteRequest.delivery_deadline && (
                                    <div className="quote-detail__info-section">
                                        <div className="quote-detail__info-label">Fecha límite de entrega</div>
                                        <div className="quote-detail__info-value">
                                            {new Date(quoteRequest.delivery_deadline).toLocaleDateString("es-AR")}
                                        </div>
                                    </div>
                                )}

                                {/* Notas */}
                                {quoteRequest.notes && (
                                    <div className="quote-detail__info-section">
                                        <div className="quote-detail__info-label">Notas</div>
                                        <div className="quote-detail__info-value">
                                            {quoteRequest.notes}
                                        </div>
                                    </div>
                                )}

                                {/* Stats */}
                                <div className="quote-detail__stats">
                                    <div className="quote-detail__stat">
                                        <div className="quote-detail__stat-value">
                                            {quoteRequest.detected_providers?.length || 0}
                                        </div>
                                        <div className="quote-detail__stat-label">Proveedores</div>
                                    </div>
                                    <div className="quote-detail__stat">
                                        <div className="quote-detail__stat-value">
                                            {respondedCount}/{responses.length}
                                        </div>
                                        <div className="quote-detail__stat-label">Respuestas</div>
                                    </div>
                                    <div className="quote-detail__stat">
                                        <div className="quote-detail__stat-value">{getTotalItems()}</div>
                                        <div className="quote-detail__stat-label">Items</div>
                                    </div>
                                </div>
                            </Card>
                        </Col>

                        {/* Cotizaciones de proveedores */}
                        <Col xs={24} lg={16}>
                            <Card
                                className="quote-detail__card"
                                title={
                                    <div className="quote-detail__table-title">
                                        <span>Cotizaciones de Proveedores</span>
                                        {respondedCount > 0 && (
                                            <Tag color="green">{respondedCount} recibidas</Tag>
                                        )}
                                    </div>
                                }
                            >
                                {renderQuoteCards()}
                            </Card>
                        </Col>
                    </Row>

                    <div className="quote-detail__analysis" ref={analysisRef}>
                        <Card className="quote-detail__ai-card">
                            <div className="quote-detail__ai-header">
                                <div>
                                    <p className="quote-detail__ai-eyebrow">Analisis con IA</p>
                                    <h2 className="quote-detail__ai-title">
                                        Donde comprar cada producto
                                    </h2>
                                    <p className="quote-detail__ai-subtitle">
                                        Define los criterios y obten un comparativo por producto con la
                                        mejor cotización disponible.
                                    </p>
                                </div>
                                <div className="quote-detail__ai-actions">
                                    <Button
                                        type="primary"
                                        onClick={handleAnalyzeQuotes}
                                        loading={analysisLoading}
                                        disabled={
                                            analysisLoading ||
                                            !analysisPrompt.trim() ||
                                            respondedCount === 0
                                        }
                                    >
                                        <span className="quote-detail__ai-cta">
                                            <SendOutlined />
                                            Solicitar análisis
                                        </span>
                                    </Button>
                                </div>
                            </div>
                            <div className="quote-detail__ai-body">
                                <div className="quote-detail__ai-field">
                                    <Text strong>Prompt de análisis</Text>
                                    <Input.TextArea
                                        value={analysisPrompt}
                                        onChange={(event) => setAnalysisPrompt(event.target.value)}
                                        placeholder="Ej: priorizar tiempo de entrega y condiciones de pago."
                                        autoSize={{ minRows: 4, maxRows: 6 }}
                                        maxLength={500}
                                        showCount
                                    />
                                    <p className="quote-detail__ai-hint">
                                        Describe los criterios para elegir donde comprar cada producto.
                                    </p>
                                </div>
                                <div className="quote-detail__ai-suggestions">
                                    <div className="quote-detail__ai-suggestions-header">
                                        <Text strong>Aspectos sugeridos</Text>
                                        <Button
                                            type="link"
                                            size="small"
                                            className="quote-detail__ai-clear"
                                            onClick={handleClearAspects}
                                            disabled={selectedAspects.length === 0}
                                        >
                                            Limpiar seleccion
                                        </Button>
                                    </div>
                                    <div className="quote-detail__ai-tags">
                                        {suggestedAspects.map((aspect) => (
                                            <Tag.CheckableTag
                                                key={aspect}
                                                checked={selectedAspects.includes(aspect)}
                                                onChange={(checked) =>
                                                    handleAspectToggle(aspect, checked)
                                                }
                                            >
                                                {aspect}
                                            </Tag.CheckableTag>
                                        ))}
                                    </div>
                                    <p className="quote-detail__ai-hint">
                                        Podés combinar varios aspectos para orientar el análisis.
                                    </p>
                                </div>
                            </div>
                            {respondedCount === 0 && (
                                <Alert
                                    className="quote-detail__ai-alert"
                                    type="info"
                                    showIcon
                                    message="Aún no hay cotizaciones respondidas para analizar."
                                />
                            )}
                        </Card>

                        <Card className="quote-detail__ai-result-card">
                            <div className="quote-detail__ai-result-header">
                                <div>
                                    <p className="quote-detail__ai-eyebrow">Resultado del análisis</p>
                                    <h2 className="quote-detail__ai-title">
                                        Mejor cotización por producto
                                    </h2>
                                </div>
                                <Tag color={analysisStatus.color} className="quote-detail__ai-status">
                                    {analysisStatus.label}
                                </Tag>
                            </div>
                            <div className="quote-detail__ai-result-body">
                                <div className="quote-detail__ai-result-layout">
                                    <div className="quote-detail__ai-result-panel">
                                        <div className="quote-detail__ai-panel-header">
                                            <Text strong>Resumen</Text>
                                            <Tag color="blue">IA</Tag>
                                        </div>
                                        {analysisLoading ? (
                                            <div className="quote-detail__ai-loading">
                                                <Spin size="large" />
                                                <p className="quote-detail__ai-hint">
                                                    Generando el analisis...
                                                </p>
                                            </div>
                                        ) : analysisResult ? (
                                            <div className="quote-detail__ai-result-content">
                                                <div className="quote-detail__ai-result-text">
                                                    {renderAnalysisContent(analysisResult)}
                                                </div>
                                                {analysisMeta && (
                                                    <div className="quote-detail__ai-result-meta">
                                                        {analysisMeta.count ? (
                                                            <span>
                                                                Cotizaciones: {analysisMeta.count}
                                                            </span>
                                                        ) : null}
                                                        {analysisMeta.model ? (
                                                            <span>Modelo: {analysisMeta.model}</span>
                                                        ) : null}
                                                        {analysisMeta.generatedAt ? (
                                                            <span>
                                                                Actualizado:{" "}
                                                                {analysisMeta.generatedAt.toLocaleString(
                                                                    "es-AR"
                                                                )}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="quote-detail__ai-result-empty">
                                                <Empty
                                                    description={
                                                        analysisError ||
                                                        "Aun no hay respuesta del agente."
                                                    }
                                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                />
                                                <p className="quote-detail__ai-hint">
                                                    {analysisError
                                                        ? "Revisa el prompt e intenta nuevamente."
                                                        : "Cuando solicites el analisis, se mostrara aqui."}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="quote-detail__ai-result-panel quote-detail__ai-result-panel--products">
                                        <div className="quote-detail__ai-panel-header">
                                            <Text strong>Productos recomendados</Text>
                                            <Tag color="green">
                                                {recommendedProducts.length} productos
                                            </Tag>
                                        </div>
                                        {analysisLoading ? (
                                            <div className="quote-detail__ai-loading">
                                                <Spin size="large" />
                                                <p className="quote-detail__ai-hint">
                                                    Armando el comparativo por producto...
                                                </p>
                                            </div>
                                        ) : recommendedProducts.length > 0 ? (
                                            <>
                                                <div className="quote-detail__ai-product-list">
                                                    {recommendedProducts.map((product, index) => {
                                                        const requestedName =
                                                            product?.requested_product_name ||
                                                            product?.product ||
                                                            product?.name ||
                                                            `Producto ${index + 1}`
                                                        const displayName = product?.product_name || requestedName
                                                        const quotedName = product?.quoted_product_name
                                                        const isSimilar =
                                                            typeof product?.is_similar_product === "boolean"
                                                                ? product.is_similar_product
                                                                : quotedName && quotedName !== requestedName
                                                        const alternativeOf =
                                                            product?.alternative_of ||
                                                            product?.requested_original ||
                                                            product?.original_product_name
                                                        const bestQuoteId =
                                                            product?.best_quote_id ||
                                                            product?.quote_id ||
                                                            product?.best_quote
                                                        const bestSupplierRaw =
                                                            product?.best_supplier ||
                                                            product?.supplier ||
                                                            product?.provider
                                                        const bestPrice = formatBestPrice(
                                                            product?.best_price || product?.price
                                                        )
                                                        const reason =
                                                            product?.reason ||
                                                            product?.rationale ||
                                                            product?.motivo
                                                        const productKey =
                                                            product?.key || `${displayName}-${index}`
                                                        const selection = orderSelections[productKey] || {}
                                                        const quotes = Array.isArray(product?.quotes)
                                                            ? product.quotes
                                                            : []
                                                        const hasQuotes = quotes.length > 0
                                                        const selectedQuote = resolveSelectedQuote(
                                                            product,
                                                            selection
                                                        )
                                                        const bestSupplier = resolveProviderLabel(
                                                            selectedQuote?.providerId || bestQuoteId,
                                                            bestSupplierRaw || selectedQuote?.providerName
                                                        )
                                                        const selectedProviderId =
                                                            selection.providerId ||
                                                            selectedQuote?.providerId ||
                                                            undefined
                                                        const selectedPriceLabel = selectedQuote
                                                            ? formatPrice(
                                                                selectedQuote.price,
                                                                selectedQuote.currency
                                                            )
                                                            : "-"
                                                        const providerOptions = quotes.map((quote) => ({
                                                            value: quote.providerId,
                                                            label: `${quote.providerLabel || quote.providerName} - ${formatPrice(
                                                                quote.price,
                                                                quote.currency
                                                            )}`
                                                        }))

                                                        return (
                                                            <div
                                                                className="quote-detail__ai-product-card"
                                                                key={productKey}
                                                            >
                                                                <div className="quote-detail__ai-product-header">
                                                                    <div className="quote-detail__ai-product-name">
                                                                        {displayName}
                                                                        {isSimilar && (
                                                                            <Tag
                                                                                color="blue"
                                                                                style={{
                                                                                    marginInlineStart: 8
                                                                                }}
                                                                            >
                                                                                Similar
                                                                            </Tag>
                                                                        )}
                                                                    </div>
                                                                    {isSimilar && alternativeOf ? (
                                                                        <div
                                                                            style={{
                                                                                fontSize: 12,
                                                                                color: "#666",
                                                                                marginTop: 4
                                                                            }}
                                                                        >
                                                                            Alternativa de: {alternativeOf}
                                                                        </div>
                                                                    ) : null}
                                                                    {bestPrice ? (
                                                                        <Tag color="green">
                                                                            Mejor {bestPrice}
                                                                        </Tag>
                                                                    ) : (
                                                                        <Tag color="default">Sin precio</Tag>
                                                                    )}
                                                                </div>
                                                                <div className="quote-detail__ai-product-best">
                                                                    <span className="quote-detail__ai-product-best-label">
                                                                        Mejor cotizacion
                                                                    </span>
                                                                    <div className="quote-detail__ai-product-best-meta">
                                                                        {bestSupplier ? (
                                                                            <span>{bestSupplier}</span>
                                                                        ) : null}
                                                                        {bestQuoteId ? (
                                                                            <span>
                                                                                Presupuesto {bestQuoteId}
                                                                            </span>
                                                                        ) : null}
                                                                        <span>
                                                                            {bestPrice || "Sin precio"}
                                                                        </span>
                                                                    </div>
                                                                    {isSimilar && quotedName && quotedName !== displayName ? (
                                                                        <div
                                                                            style={{
                                                                                fontSize: 12,
                                                                                color: "#666",
                                                                                marginTop: 4
                                                                            }}
                                                                        >
                                                                            Cotizado como: {quotedName}
                                                                        </div>
                                                                    ) : null}
                                                                    {reason ? (
                                                                        <p className="quote-detail__ai-product-reason">
                                                                            {reason}
                                                                        </p>
                                                                    ) : null}
                                                                </div>
                                                                <div className="quote-detail__ai-product-order">
                                                                    <div className="quote-detail__ai-product-order-row">
                                                                        <Checkbox
                                                                            checked={Boolean(selection.selected)}
                                                                            onChange={(event) =>
                                                                                handleToggleOrderItem(
                                                                                    productKey,
                                                                                    event.target.checked
                                                                                )
                                                                            }
                                                                            disabled={!hasQuotes}
                                                                        >
                                                                            Agregar al pedido
                                                                        </Checkbox>
                                                                        <Select
                                                                            className="quote-detail__ai-product-order-select"
                                                                            placeholder="Proveedor"
                                                                            value={selectedProviderId}
                                                                            onChange={(value) =>
                                                                                handleOrderProviderChange(
                                                                                    productKey,
                                                                                    value,
                                                                                    product
                                                                                )
                                                                            }
                                                                            options={providerOptions}
                                                                            disabled={!hasQuotes}
                                                                        />
                                                                    </div>
                                                                    <div className="quote-detail__ai-product-order-meta">
                                                                        <span>
                                                                            Cantidad: {product?.quantity ?? "-"}
                                                                        </span>
                                                                        {product?.measure ? (
                                                                            <span>{product.measure}</span>
                                                                        ) : null}
                                                                        <span>{selectedPriceLabel}</span>
                                                                    </div>
                                                                    {!hasQuotes ? (
                                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                                            Sin cotizacion para este producto.
                                                                        </Text>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                                <div className="quote-detail__ai-order-panel">
                                                    <div className="quote-detail__ai-order-header">
                                                        <Text strong>Pedido por proveedor</Text>
                                                        <Tag color={orderSummaryData.selectedCount ? "green" : "default"}>
                                                            {orderSummaryData.selectedCount} seleccionados
                                                        </Tag>
                                                    </div>
                                                    <div className="quote-detail__ai-order-body">
                                                        {orderSummaryData.selectedCount ? (
                                                            <>
                                                                <div className="quote-detail__ai-order-meta">
                                                                    <span>{orderSummaryData.providerCount} proveedores</span>
                                                                    <span>{orderSummaryData.selectedCount} productos</span>
                                                                </div>
                                                                <div className="quote-detail__ai-order-list">
                                                                    {orderSummaryData.providers.map((provider) => (
                                                                        <div className="quote-detail__ai-order-provider" key={provider.providerId}>
                                                                            <span>{provider.providerName}</span>
                                                                            <span>{provider.items.length} productos</span>
                                                                            {provider.totalValid && provider.currency ? (
                                                                                <span>
                                                                                    Total: {formatPrice(provider.total, provider.currency)}
                                                                                </span>
                                                                            ) : (
                                                                                <span>Total: N/A</span>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <Text type="secondary">Selecciona productos para armar pedidos.</Text>
                                                        )}
                                                    </div>
                                                    {orderSummary ? (
                                                        <Alert
                                                            type="success"
                                                            showIcon
                                                            message="Pedidos enviados"
                                                            description={`Pedidos generados: ${orderSummary.total_orders || orderSummary.totalOrders || orderSummary.orders?.length || 0}`}
                                                        />
                                                    ) : null}
                                                    <div className="quote-detail__ai-order-actions">
                                                        <Button
                                                            size="small"
                                                            onClick={handleSelectAllOrders}
                                                            disabled={!recommendedProducts.length}
                                                        >
                                                            Seleccionar todos
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            onClick={handleClearOrderSelection}
                                                            disabled={!orderSummaryData.selectedCount}
                                                        >
                                                            Limpiar
                                                        </Button>
                                                        <Button
                                                            type="primary"
                                                            icon={<SendOutlined />}
                                                            onClick={handleCreateOrders}
                                                            loading={orderSubmitting}
                                                            disabled={!orderSummaryData.selectedCount}
                                                        >
                                                            Enviar pedidos
                                                        </Button>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="quote-detail__ai-result-empty">
                                                <Empty
                                                    description={
                                                        analysisResult
                                                            ? "El agente no devolvio recomendaciones por producto."
                                                            : "Aun no hay productos para comparar."
                                                    }
                                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                />
                                                <p className="quote-detail__ai-hint">
                                                    Ejecuta el analisis para obtener recomendaciones.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </Content>
        </Layout>
    )
}

export default QuoteRequestDetail
